// SalesReport.js
import React, { useEffect, useMemo, useState } from "react";
import { api } from "./api"; // your axios instance that already sets baseURL
// IMPORTANT: X-Store-ID must be set globally (setStoreIdHeader(storeId)) or in interceptor

export default function SalesReport({ onBack }) {
  const today = () => new Date().toISOString().slice(0, 10);

  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(today);

  const [groupBy, setGroupBy] = useState("cashier"); // cashier|day
  const [cashier, setCashier] = useState("");
  const [printed, setPrinted] = useState("all"); // all|yes|no
  const [q, setQ] = useState("");

  const [summary, setSummary] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  const totals = useMemo(() => {
    let qty = 0, total = 0;
    for (const r of items) {
      qty += Number(r.qty || 0);
      total += Number(r.line_total || 0);
    }
    return { qty, total };
  }, [items]);

  async function load() {
    setLoading(true);
    setMsg("");
    try {
      // 1) Summary
      const s = await api.get("/server.php/reports/sales-summary", {
        params: { from, to, group_by: groupBy },
      });

      // 2) Detailed items
      const it = await api.get("/server.php/reports/sale-items", {
        params: { from, to, cashier, printed, q },
      });

      setSummary(Array.isArray(s.data) ? s.data : []);
      setItems(Array.isArray(it.data) ? it.data : []);
    } catch (e) {
      setMsg(e?.response?.data?.message || e.message || "Load failed");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* initial */ }, []); // eslint-disable-line

  function printReport() {
    window.print();
  }

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
        <button onClick={onBack}>Back</button>
        <h2 style={{ margin: 0 }}>Sales Report</h2>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button onClick={load} disabled={loading}>Refresh</button>
          <button onClick={printReport}>Print</button>
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
        <label>
          From
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          To
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label>
          Group By
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
            <option value="cashier">Cashier</option>
            <option value="day">Day</option>
          </select>
        </label>
        <label>
          Cashier (optional)
          <input value={cashier} onChange={(e) => setCashier(e.target.value)} placeholder="e.g. kevin" />
        </label>
        <label>
          Printed
          <select value={printed} onChange={(e) => setPrinted(e.target.value)}>
            <option value="all">All</option>
            <option value="yes">Printed</option>
            <option value="no">Not printed</option>
          </select>
        </label>
        <label>
          Search
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="item / cashier / sale_id" />
        </label>
      </div>

      {msg ? <div style={{ color: "crimson", marginBottom: 10 }}>{msg}</div> : null}
      {loading ? <div style={{ marginBottom: 10 }}>Loading…</div> : null}

      <div style={{ marginBottom: 16 }}>
        <h3 style={{ margin: "8px 0" }}>Summary ({groupBy})</h3>
        <table width="100%" cellPadding="6" style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th align="left" style={{ borderBottom: "1px solid #ccc" }}>{groupBy === "day" ? "Day" : "Cashier"}</th>
              <th align="right" style={{ borderBottom: "1px solid #ccc" }}>Bills</th>
              <th align="right" style={{ borderBottom: "1px solid #ccc" }}>Total Sales</th>
              <th align="right" style={{ borderBottom: "1px solid #ccc" }}>Total Paid</th>
            </tr>
          </thead>
          <tbody>
            {summary.map((r, idx) => (
              <tr key={idx}>
                <td style={{ borderBottom: "1px solid #eee" }}>{r.day ?? r.cashier ?? ""}</td>
                <td align="right" style={{ borderBottom: "1px solid #eee" }}>{r.bills ?? 0}</td>
                <td align="right" style={{ borderBottom: "1px solid #eee" }}>{Number(r.total_sales ?? 0).toFixed(2)}</td>
                <td align="right" style={{ borderBottom: "1px solid #eee" }}>{Number(r.total_paid ?? 0).toFixed(2)}</td>
              </tr>
            ))}
            {!summary.length ? (
              <tr><td colSpan="4" style={{ padding: 10, color: "#666" }}>No summary rows.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <div>
        <h3 style={{ margin: "8px 0" }}>Detailed Items</h3>
        <div style={{ marginBottom: 8, color: "#333" }}>
          Total Qty: <b>{totals.qty}</b> &nbsp; | &nbsp; Total Amount: <b>{totals.total.toFixed(2)}</b>
        </div>
        <table width="100%" cellPadding="6" style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th align="left" style={{ borderBottom: "1px solid #ccc" }}>Date</th>
              <th align="right" style={{ borderBottom: "1px solid #ccc" }}>Sale#</th>
              <th align="left" style={{ borderBottom: "1px solid #ccc" }}>Item</th>
              <th align="right" style={{ borderBottom: "1px solid #ccc" }}>Qty</th>
              <th align="right" style={{ borderBottom: "1px solid #ccc" }}>Unit</th>
              <th align="right" style={{ borderBottom: "1px solid #ccc" }}>Line Total</th>
              <th align="left" style={{ borderBottom: "1px solid #ccc" }}>Cashier</th>
              <th align="left" style={{ borderBottom: "1px solid #ccc" }}>Printed</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id}>
                <td style={{ borderBottom: "1px solid #eee" }}>{String(r.created_at || "").slice(0, 19)}</td>
                <td align="right" style={{ borderBottom: "1px solid #eee" }}>{r.sale_id}</td>
                <td style={{ borderBottom: "1px solid #eee" }}>{r.item_name}</td>
                <td align="right" style={{ borderBottom: "1px solid #eee" }}>{r.qty}</td>
                <td align="right" style={{ borderBottom: "1px solid #eee" }}>{Number(r.unit_price || 0).toFixed(2)}</td>
                <td align="right" style={{ borderBottom: "1px solid #eee" }}>{Number(r.line_total || 0).toFixed(2)}</td>
                <td style={{ borderBottom: "1px solid #eee" }}>{r.cashier || ""}</td>
                <td style={{ borderBottom: "1px solid #eee" }}>
                  {r.printed_at ? `Yes (${String(r.printed_at).slice(0,19)})` : "No"}
                </td>
              </tr>
            ))}
            {!items.length ? (
              <tr><td colSpan="8" style={{ padding: 10, color: "#666" }}>No sale items in range.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}